// packages
import cx from 'classnames';
import { h } from 'preact';

// components
import CandidateRow from './CandidateRow';
import Contest from './Contest';
import DistrictInfo from './DistrictInfo';
import SeatPartyDivider from './SeatPartyDivider';
import Portal from './Portal';
import PureComponent from './PureComponent';
import Typeahead from './Typeahead';
import CountyInfo from './CountyInfo';
import LocalRaces from './LocalRaces';
import asyncComponent from './AsyncComponent';

// Lazy-loaded so the district map code (d3, turf, etc.) isn't in the initial bundle
const AsyncDistrictMap = asyncComponent(() =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "DistrictMap" */ './DistrictMapNew'
  )
);

// Lazy-loaded district demographics/turnout stats (eligible voters, race
// breakdown, 2024 turnout, previous-results chart)
const AsyncDistrictStats = asyncComponent(() =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "DistrictStats" */ './DistrictStatsNew'
  )
);

// internal
import debounce from '../utils/debounce';
import { determineContestTitleYourRaces } from '../election-utils';
import { lookup } from '../utils/legislative-districts';
import {
  createBallot,
  filterDownBallotRaces,
  filterLocalMeasures,
  getLocalRaceTitle,
  LOCAL_RACE_GROUPS,
} from '../utils/branch-ballot';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

let PlacesLib = null; // { AutocompleteSessionToken, AutocompleteSuggestion, Place } loaded via importLibrary()

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const toLocationRestriction = b => ({
  west: b.west,
  east: b.east,
  south: b.south,
  north: b.north,
});

const getPredictions = async (input, sessionToken) => {
  if (!input || input.trim().length < 2) return [];

  const request = {
    input,
    locationRestriction: toLocationRestriction(bounds),
    includedPrimaryTypes: ['street_address', 'premise', 'route'],
    region: 'us',
    sessionToken,
  };

  const {
    suggestions,
  } = await PlacesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
    request
  );

  return suggestions.map(s => ({
    id: s.placePrediction.placeId,
    description: s.placePrediction.text.toString(),
    place_id: s.placePrediction.placeId,
    _prediction: s.placePrediction,
  }));
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

// Place.location is a LatLng whose lat/lng are functions in some versions of
// the Places library and plain numbers in others.
const toCoordinates = latLng => ({
  latitude: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
  longitude: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
});

class YourRaces extends PureComponent {
  constructor() {
    super();

    this.state = {
      contests: [],
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      results: [],
      value: '',
      selectedPlaceId: null, // Chosen from the suggestions list; lookup runs on submit
      bodyWidth: null,
      width: null,
      branchBallot: null, // Down-ballot local races/candidates from Branch
      branchPending: false, // true from submit until Branch responds (or fails)
      activeRaceFilters: [], // empty = "All" — show every race
      raceSearchQuery: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onJumpToAllRaces = this.onJumpToAllRaces.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.toggleRaceFilter = this.toggleRaceFilter.bind(this);
    this.onRaceSearchChange = this.onRaceSearchChange.bind(this);
    this.sessionToken = null;
    this.branchRequestId = 0; // lets a stale Branch response be ignored
    this.selectedPrediction = null; // _prediction for the selected suggestion, used at submit
    this.branchPrefetch = null; // { placeId, promise } — Branch request started on selection
  }

  // Calculate responsive widths for the district map, based on browser size
  getContainerWidth() {
    const bodyWidth = window.innerWidth;
    const width =
      bodyWidth > 543 ? 220 : bodyWidth > 330 ? 280 : bodyWidth - 70;
    this.setState({
      width: width,
      bodyWidth: bodyWidth,
    });
  }

  // Clears the prefetched ZIP when a selection is invalidated, so a stale
  // value from a previous address can't tag along on a later submission.
  clearZip() {
    const zipInput = document.getElementById('signup-zip');
    if (zipInput) zipInput.value = '';
  }

  onClear(value) {
    this.setState({ value: '', selectedPlaceId: null });
    this.selectedPrediction = null;
    this.clearZip();
  }

  // "all" is exclusive with every other filter
  toggleRaceFilter(filter) {
    this.setState(({ activeRaceFilters }) => {
      if (filter === 'all') return { activeRaceFilters: [] };

      const activeWithoutAll = activeRaceFilters.filter(f => f !== 'all');
      const isActive = activeWithoutAll.includes(filter);

      return {
        activeRaceFilters: isActive
          ? activeWithoutAll.filter(f => f !== filter)
          : [...activeWithoutAll, filter],
      };
    });
  }

  onJumpToAllRaces(event) {
    event.preventDefault();
    const heading = document.getElementById('all-races__heading');
    if (!heading) return;

    heading.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const clearBodyOverflowLock = () => {
      if (document.body.style.overflow) document.body.style.overflow = '';
    };
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', clearBodyOverflowLock, {
        once: true,
      });
    } else {
      setTimeout(clearBodyOverflowLock, 600);
    }
  }

  onRaceSearchChange(value) {
    this.setState({ raceSearchQuery: value });
  }

  onChange(value) {
    this.setState(
      { value, selectedPlaceId: null },
      this.debouncedHandleGeocode
    );
    this.selectedPrediction = null;
    this.clearZip();
    // Start a new autocomplete session when the user begins typing
    if (!this.sessionToken && PlacesLib) {
      this.sessionToken = new PlacesLib.AutocompleteSessionToken();
    }
  }

  async handleGeocode() {
    if (!PlacesLib) return;
    const { value: input } = this.state;
    try {
      const results = await getPredictions(input, this.sessionToken);
      this.setState({ results });
    } catch (e) {
      this.setState({ results: [] });
    }
  }

  onSelect({ name, placeId, _prediction }) {
    this.setState({
      value: name,
      selectedPlaceId: placeId,
      results: [],
      lookupError: false,
    });
    this.selectedPrediction = _prediction;

    this.prefetchSelectedPlace(placeId, _prediction);
  }

  // As soon as an address is picked (before the email/submit step), fetch its
  // details once and use them for both the signup form's ZIP and a head start
  // on the Branch request — so local races are usually ready by the time the
  // reader submits.
  prefetchSelectedPlace(placeId, prediction) {
    if (!PlacesLib) return;

    const placePromise = (async () => {
      const place = prediction
        ? prediction.toPlace()
        : new PlacesLib.Place({ id: placeId });

      await place.fetchFields({
        fields: ['location', 'formattedAddress', 'addressComponents'],
      });

      return place;
    })();

    this.prefetchZip(placePromise);
    this.branchPrefetch = {
      placeId,
      promise: this.prefetchBranchBallot(placePromise),
    };
  }

  // Grabs the ZIP for the email signup form's hidden field
  async prefetchZip(placePromise) {
    const zipInput = document.getElementById('signup-zip');
    if (!zipInput) return;

    try {
      const place = await placePromise;

      const zipComponent = (place.addressComponents || []).find(component =>
        component.types.includes('postal_code')
      );
      zipInput.value = zipComponent ? zipComponent.longText : '';
    } catch (e) {
      // Non-critical enhancement — leave the field blank if this fails.
    }
  }

  // Resolves to the Branch ballot, or null if anything fails — never rejects,
  // so an unused prefetch can't surface as an unhandled rejection. A null is
  // retried from scratch on submit (see loadBranchBallot).
  async prefetchBranchBallot(placePromise) {
    try {
      const place = await placePromise;

      return await createBallot(
        this.buildBranchLocation(place, toCoordinates(place.location))
      );
    } catch (e) {
      console.warn('Branch prefetch failed', e);
      return null;
    }
  }

  // build the Branch location from an already-fetched (geocoded) Google Place
  buildBranchLocation(place, coordinates) {
    const components = place.addressComponents || [];
    const findComponent = type => {
      const match = components.find(c => c.types.includes(type));
      return match ? match.longText : undefined;
    };

    const stateComponent = components.find(c =>
      c.types.includes('administrative_area_level_1')
    );

    // Branch's schema types zip as an integer; omit it if Google didn't
    // return a usable one.
    const zip = parseInt(findComponent('postal_code'), 10);

    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      address: place.formattedAddress,
      addressComponents: {
        city: findComponent('locality'),
        county: findComponent('administrative_area_level_2'),
        state: stateComponent ? stateComponent.shortText : undefined,
        zip: Number.isNaN(zip) ? undefined : zip,
      },
    };
  }

  // get down-ballot local races from Branch — reusing the request already
  // started when this address was selected, if there is one.
  async loadBranchBallot(place, coordinates, placeId) {
    const requestId = ++this.branchRequestId;
    let branchBallot = null;

    try {
      const prefetch = this.branchPrefetch;
      if (prefetch && prefetch.placeId === placeId) {
        branchBallot = await prefetch.promise;
      }

      // No usable prefetch (never started, or it failed) — ask now.
      if (!branchBallot) {
        await place.fetchFields({
          fields: ['formattedAddress', 'addressComponents'],
        });

        branchBallot = await createBallot(
          this.buildBranchLocation(place, coordinates)
        );
      }
    } catch (e) {
      console.warn('Branch ballot request failed', e);
      branchBallot = null;
    }

    // A newer submit has replaced this one — drop the stale response.
    if (requestId !== this.branchRequestId) return;

    // Filter buttons only exist for groups that have races at this address, so
    // drop any leftover filter from a previous address rather than strand it.
    const localRaces = branchBallot
      ? filterDownBallotRaces(branchBallot.races)
      : [];
    const emptyGroupKeys = LOCAL_RACE_GROUPS.filter(
      group => !localRaces.some(group.matches)
    ).map(group => group.key);
    if (
      !branchBallot ||
      filterLocalMeasures(branchBallot.measures).length === 0
    ) {
      emptyGroupKeys.push('local-measures');
    }

    this.setState(({ activeRaceFilters }) => ({
      branchBallot,
      branchPending: false,
      activeRaceFilters: activeRaceFilters.filter(
        filter => !emptyGroupKeys.includes(filter)
      ),
    }));
  }

  // run the district lookup for whichever address was selected from the
  // dropdown.
  async onSubmit(event) {
    event.preventDefault();

    // Same native validation UI as the email field below — catches hitting
    // Enter/submit with the address field left empty.
    const addressInput = document.getElementById('address-input');
    if (addressInput && !addressInput.checkValidity()) {
      addressInput.reportValidity();
      return;
    }

    const { selectedPlaceId } = this.state;
    if (!selectedPlaceId) return;

    // Email is mandatory to see personalized results — block here (with the
    // browser's native validation UI) before running the district lookup.
    const emailInput = document.getElementById('signup-email');
    if (emailInput && !emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    // Clear the previous address's local races so they don't linger next to
    // the new address's results while Branch is still responding.
    this.setState({
      lookupError: false,
      lookupPending: true,
      branchBallot: null,
      branchPending: true,
    });

    try {
      const place = this.selectedPrediction
        ? this.selectedPrediction.toPlace()
        : new PlacesLib.Place({ id: selectedPlaceId });

      await place.fetchFields({ fields: ['location'] });

      const coordinates = toCoordinates(place.location);

      // Reset session so the next keystroke starts a fresh token
      this.sessionToken = null;

      lookup(coordinates)
        .then(({ data }) => {
          // The SBOE button is hidden when this address has no SBOE race, so
          // don't leave its filter stuck on from a previous address.
          const hasSboeRace =
            data.sboe &&
            this.props.contests.some(
              contest =>
                contest.office === 'State Board of Education' &&
                contest.office_division === data.sboe.district
            );

          this.setState(({ activeRaceFilters }) => ({
            coordinates,
            data,
            lookupPending: false,
            lookedFoundStored: false,
            activeRaceFilters: hasSboeRace
              ? activeRaceFilters
              : activeRaceFilters.filter(filter => filter !== 'education'),
          }));
        })
        .catch(e => {
          console.error('District lookup failed', { coordinates, error: e });
          this.setState({
            data: null,
            lookupError: true,
            lookupPending: false,
          });
        });

      // Local races (Branch/VoterGuideOS)
      this.loadBranchBallot(place, coordinates, selectedPlaceId);
    } catch (e) {
      console.error('Google place details failed', {
        placeId: selectedPlaceId,
        error: e,
      });
      this.setState({
        data: null,
        lookupError: true,
        lookupPending: false,
        branchPending: false,
      });
      this.sessionToken = null;
    }
  }

  async componentDidMount() {
    PlacesLib = await google.maps.importLibrary('places');

    this.getContainerWidth();
    window.addEventListener('resize', this.getContainerWidth);

    // // If we ever wanted to show a default location, coordinates for Governor mansion in Austin
    // let latitude = 30.2730665;
    // let longitude = -97.74341629999999;
    // let coordinates = { latitude, longitude };

    // lookup(coordinates)
    //   .then(({ data }) => {
    //     this.setState({
    //       coordinates,
    //       data,
    //       lookupPending: false,
    //       lookedFoundStored: false,
    //     });
    //   })
    //   .catch(() =>
    //     this.setState({
    //       data: null,
    //       lookupError: true,
    //       lookupPending: false,
    //     })
    //   );
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.getContainerWidth);
  }

  render() {
    const { contests, notes } = this.props;
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      results,
      value,
      bodyWidth,
      width,
      activeRaceFilters,
      raceSearchQuery,
      branchBallot,
      branchPending,
    } = this.state;

    const shouldShowRace = filter =>
      activeRaceFilters.length === 0 || activeRaceFilters.includes(filter);

    // Down-ballot races from Branch — arrive after the district lookup, so
    // this is empty until then (or if Branch fails).
    const localRaces = branchBallot
      ? filterDownBallotRaces(branchBallot.races)
      : [];
    const localMeasures = branchBallot
      ? filterLocalMeasures(branchBallot.measures)
      : [];
    const localRacesByGroup = {};
    LOCAL_RACE_GROUPS.forEach(group => {
      localRacesByGroup[group.key] = localRaces.filter(group.matches);
    });

    // Loose match
    const normalizeForSearch = str =>
      str.toLowerCase().replace(/[^a-z0-9]/g, '');

    const raceSearchTerm = normalizeForSearch(raceSearchQuery.trim());
    const matchesRaceSearch = title =>
      !raceSearchTerm || normalizeForSearch(title).includes(raceSearchTerm);

    const raceSearchTitles = data && {
      'us-house': `U.S. House District ${data.us_house.district}`,
      'tx-senate': `Texas Senate District ${data.tx_senate.district}`,
      'tx-house': `Texas House District ${data.tx_house.district}`,
      sboe: `State Board of Education District ${data.sboe.district}`,
    };

    // Local races and measures come from a separate (later) request, so they
    // join the suggestions once Branch has responded.
    const localRaceSearchItems = [
      ...localRaces.map(race => ({
        key: race._id,
        title: getLocalRaceTitle(race),
      })),
      ...localMeasures.map(measure => ({
        key: measure._id,
        title: measure.title,
      })),
    ];

    const raceSearchItems = raceSearchTitles && [
      { key: 'us-house', title: raceSearchTitles['us-house'] },
      { key: 'tx-senate', title: raceSearchTitles['tx-senate'] },
      { key: 'tx-house', title: raceSearchTitles['tx-house'] },
      { key: 'sboe', title: raceSearchTitles.sboe },
      ...localRaceSearchItems,
    ];

    let addressFeedback = null;

    const matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const noteMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    if (data) {
      matches['us-house'] = contests.filter(
        contest =>
          contest.office === 'U.S. House' &&
          contest.office_division === data.us_house.district
      );

      matches['sboe'] = contests.filter(
        contest =>
          contest.office === 'State Board of Education' &&
          contest.office_division === data.sboe.district
      );

      matches['tx-senate'] = contests.filter(
        contest =>
          contest.office === 'Texas Senate' &&
          contest.office_division === data.tx_senate.district
      );

      matches['tx-house'] = contests.filter(
        contest =>
          contest.office === 'Texas House' &&
          contest.office_division === data.tx_house.district
      );

      noteMatches['us-house'] = notes.filter(
        contest =>
          contest.type === 'us-house-district-' + data.us_house.district
      );

      noteMatches['sboe'] = notes.filter(
        contest =>
          contest.type ===
          'state-board-of-education-district-' + data.sboe.district
      );

      noteMatches['tx-senate'] = notes.filter(
        contest =>
          contest.type === 'texas-senate-district-' + data.tx_senate.district
      );

      noteMatches['tx-house'] = notes.filter(
        contest =>
          contest.type === 'texas-house-district-' + data.tx_house.district
      );
    }

    // Counts shown in the filter buttons below — a seat with no match (e.g.
    // "not up for election this year") doesn't count as a race.
    const raceCounts = data && {
      'us-house': matches['us-house'].length > 0 ? 1 : 0,
      'state-legislature':
        (matches['tx-senate'].length > 0 ? 1 : 0) +
        (matches['tx-house'].length > 0 ? 1 : 0),
      education: matches['sboe'].length > 0 ? 1 : 0,
    };
    if (raceCounts) {
      LOCAL_RACE_GROUPS.forEach(group => {
        raceCounts[group.key] = localRacesByGroup[group.key].length;
      });
      raceCounts['local-measures'] = localMeasures.length;
      raceCounts.all =
        raceCounts['us-house'] +
        raceCounts['state-legislature'] +
        raceCounts.education +
        LOCAL_RACE_GROUPS.reduce(
          (total, group) => total + raceCounts[group.key],
          0
        ) +
        raceCounts['local-measures'];
    }

    // Narrows local items to the race search. A title typed (or picked from
    // the suggestions) exactly wins over partial matches, so picking
    // "…Post 1" doesn't also pull in Posts 10 through 15.
    const matchLocalSearch = (items, getTitle) => {
      if (!raceSearchTerm) return items;

      const exactMatches = items.filter(
        item => normalizeForSearch(getTitle(item)) === raceSearchTerm
      );

      return exactMatches.length > 0
        ? exactMatches
        : items.filter(item => matchesRaceSearch(getTitle(item)));
    };

    // Local races that pass the active filters (county, city, education,
    // judicial) and the search
    const shownLocalRaces = matchLocalSearch(
      [].concat(
        ...LOCAL_RACE_GROUPS.filter(group => shouldShowRace(group.key)).map(
          group => localRacesByGroup[group.key]
        )
      ),
      getLocalRaceTitle
    );
    const shownLocalMeasures = matchLocalSearch(
      shouldShowRace('local-measures') ? localMeasures : [],
      measure => measure.title
    );

    // "Showing X of Y races" below the search bar — a seat with no match
    // (not up for election this year) is hidden entirely, so it shouldn't
    // count toward either number.
    const raceSlotCategories = {
      'us-house': 'us-house',
      'tx-senate': 'state-legislature',
      'tx-house': 'state-legislature',
      sboe: 'education',
    };
    const totalRaceSlots = data
      ? Object.keys(raceSlotCategories).filter(slug => matches[slug].length > 0)
          .length +
        localRaces.length +
        localMeasures.length
      : 0;
    const visibleRaceSlots = data
      ? Object.keys(raceSlotCategories).filter(
          slug =>
            matches[slug].length > 0 &&
            shouldShowRace(raceSlotCategories[slug]) &&
            matchesRaceSearch(raceSearchTitles[slug])
        ).length +
        shownLocalRaces.length +
        shownLocalMeasures.length
      : 0;

    if (lookupPending) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          We found your information! These races have been personalized for you.{' '}
          <br></br>
          <br></br>
          <span class="t-weight-normal">
            Scroll down to the <a href="#all-races__heading">all races</a>{' '}
            section to see the federal and statewide candidates on every Texan's
            ballot.
          </span>
        </p>
      );
    }

    const inputProps = {
      id: 'address-input',
      class: 'address-input__input',
      placeholder: '1010 Colorado St, Austin, TX, 78701',
      required: true,
    };

    return (
      <form
        id="address-form"
        class="address-input t-sans"
        onSubmit={this.onSubmit}
      >
        <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg>
        <label class="address-input__label" for="address-input">
          Enter your address
        </label>

        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
            _prediction: item._prediction,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img src={googleIMG} alt="Powered by Google" />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        <p class="address-input__subhead">We never sell your information.</p>

        <Portal node={document.getElementById('address-submit')}>
          <div>
            <button
              type="submit"
              ref={el => el && el.setAttribute('form', 'address-form')}
              class="button button--yellow address-input__submit"
            >
              See my ballot
            </button>
            <a
              href="#all-races__heading"
              class="address-input__jump-link t-links-unset"
              onClick={this.onJumpToAllRaces}
            >
              Or, jump to all races
            </a>
          </div>
        </Portal>

        <Portal node={document.getElementById('your-address-feedback')}>
          {addressFeedback}
        </Portal>

        {data && data.counties && (
          <Portal node={document.getElementById('local-candidates-info')}>
            <span>
              <CountyInfo
                key={data.counties.fips}
                county={data.counties.name}
                fips={data.counties.fips}
              />
            </span>
          </Portal>
        )}

        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={ref => (this._resultsPortal = ref)}
          >
            <div>
              <div
                class="your-races-filter"
                role="group"
                aria-label="Filter your races"
              >
                <button
                  type="button"
                  class={cx('your-races-filter__btn', {
                    'is-active': activeRaceFilters.length === 0,
                  })}
                  onClick={() => this.toggleRaceFilter('all')}
                >
                  All ({raceCounts.all})
                </button>
                <button
                  type="button"
                  class={cx('your-races-filter__btn', {
                    'is-active': activeRaceFilters.includes('us-house'),
                  })}
                  onClick={() => this.toggleRaceFilter('us-house')}
                >
                  U.S. House ({raceCounts['us-house']})
                </button>
                <button
                  type="button"
                  class={cx('your-races-filter__btn', {
                    'is-active': activeRaceFilters.includes(
                      'state-legislature'
                    ),
                  })}
                  onClick={() => this.toggleRaceFilter('state-legislature')}
                >
                  State Legislature ({raceCounts['state-legislature']})
                </button>
                {/* Only some SBOE districts are on the ballot each cycle */}
                {raceCounts.education > 0 && (
                  <button
                    type="button"
                    class={cx('your-races-filter__btn', {
                      'is-active': activeRaceFilters.includes('education'),
                    })}
                    onClick={() => this.toggleRaceFilter('education')}
                  >
                    SBOE ({raceCounts.education})
                  </button>
                )}
                {/* Faded placeholder while Branch is still responding; swapped
                for the real local buttons below once it has. */}
                {branchPending && (
                  <button type="button" class="your-races-filter__btn" disabled>
                    Local
                  </button>
                )}
                {/* Only offered once Branch has responded, and only for groups
                that have races at this address. */}
                {!branchPending &&
                  LOCAL_RACE_GROUPS.filter(
                    group => raceCounts[group.key] > 0
                  ).map(group => (
                    <button
                      type="button"
                      class={cx('your-races-filter__btn', {
                        'is-active': activeRaceFilters.includes(group.key),
                      })}
                      onClick={() => this.toggleRaceFilter(group.key)}
                    >
                      {group.label} ({raceCounts[group.key]})
                    </button>
                  ))}
                {!branchPending && raceCounts['local-measures'] > 0 && (
                  <button
                    type="button"
                    class={cx('your-races-filter__btn', {
                      'is-active': activeRaceFilters.includes('local-measures'),
                    })}
                    onClick={() => this.toggleRaceFilter('local-measures')}
                  >
                    Local: Measures ({raceCounts['local-measures']})
                  </button>
                )}
              </div>
              <div class="your-races-search-bar" role="search">
                <label
                  class="your-races-search-bar__label"
                  for="your-races-search"
                ></label>
                <Typeahead
                  inputProps={{
                    id: 'your-races-search',
                    type: 'search',
                    class: 'your-races-search-bar__input',
                    placeholder: 'Filter by district or race',
                  }}
                  items={raceSearchItems}
                  value={raceSearchQuery}
                  getItemValue={item => item.title}
                  onChange={this.onRaceSearchChange}
                  onClear={() => this.onRaceSearchChange('')}
                  onSelect={value => this.onRaceSearchChange(value)}
                  shouldItemRender={(item, query) =>
                    normalizeForSearch(item.title).includes(
                      normalizeForSearch(query)
                    )
                  }
                  renderItem={(item, isHighlighted) => (
                    <li
                      class={cx('listbox__item', {
                        'listbox__item--highlighted': isHighlighted,
                      })}
                      key={item.key}
                    >
                      {item.title}
                    </li>
                  )}
                  renderAttribution={() => null}
                />
              </div>
              {data && (
                <p class="your-races-results-count">
                  Showing <strong>{visibleRaceSlots}</strong> of{' '}
                  <strong>{totalRaceSlots}</strong> races.
                </p>
              )}
              <div class="representative-districts contest-list">
                {shouldShowRace('us-house') &&
                  matchesRaceSearch(raceSearchTitles['us-house']) && (
                    <Contest
                      key={matches['us-house'][0].office_slug}
                      contest={matches['us-house']}
                      contestTitle={determineContestTitleYourRaces(
                        matches['us-house'][0]
                      )}
                      notes={noteMatches['us-house'].filter(
                        item => item.type === matches['us-house'][0].office_slug
                      )}
                      redistrictingNote
                      tableHeader={CandidateRow.Header}
                      columnCount={CandidateRow.columnCount}
                      renderParty={party => (
                        <SeatPartyDivider
                          partyName={party.party}
                          partyBallot={party}
                          tableHeader={CandidateRow.Header}
                          notes={noteMatches['us-house'].filter(
                            contest =>
                              contest.type ===
                              party.office_slug + '-' + party.party
                          )}
                          columnCount={CandidateRow.columnCount}
                          renderCandidate={(candidate, idx) => (
                            <CandidateRow
                              key={candidate.id}
                              ballotName={candidate.name}
                              candidateId={candidate.id}
                              isIncumbent={candidate.is_incumbent}
                              party={candidate.party}
                            />
                          )}
                        />
                      )}
                    >
                      <DistrictInfo>
                        <AsyncDistrictStats
                          name="U.S. House"
                          district={data['us_house'].district}
                          slug="us-house"
                          width={width}
                        />
                        <AsyncDistrictMap
                          slug="us-house"
                          district={data['us_house'].district}
                          point={coordinates}
                          width={width}
                        />
                      </DistrictInfo>
                    </Contest>
                  )}
                {shouldShowRace('state-legislature') &&
                  matchesRaceSearch(raceSearchTitles['tx-senate']) &&
                  matches['tx-senate'].length > 0 && (
                    <Contest
                      key={matches['tx-senate'].id}
                      contest={matches['tx-senate']}
                      tableHeader={CandidateRow.Header}
                      columnCount={CandidateRow.columnCount}
                      notes={noteMatches['tx-senate'].filter(
                        item =>
                          item.type === matches['tx-senate'][0].office_slug
                      )}
                      contestTitle={determineContestTitleYourRaces(
                        matches['tx-senate'][0]
                      )}
                      renderParty={party => (
                        <SeatPartyDivider
                          partyName={party.party}
                          partyBallot={party}
                          tableHeader={CandidateRow.Header}
                          notes={noteMatches['tx-senate'].filter(
                            contest =>
                              contest.type ===
                              party.office_slug + '-' + party.party
                          )}
                          columnCount={CandidateRow.columnCount}
                          renderCandidate={(candidate, idx) => (
                            <CandidateRow
                              key={candidate.id}
                              ballotName={candidate.name}
                              candidateId={candidate.id}
                              isIncumbent={candidate.is_incumbent}
                              party={candidate.party}
                            />
                          )}
                        />
                      )}
                    >
                      <DistrictInfo>
                        <AsyncDistrictStats
                          name="Texas Senate"
                          district={data['tx_senate'].district}
                          slug="texas-senate"
                          width={width}
                        />
                        <AsyncDistrictMap
                          slug="texas-senate"
                          district={data['tx_senate'].district}
                          point={coordinates}
                          width={width}
                        />
                      </DistrictInfo>
                    </Contest>
                  )}
                {shouldShowRace('state-legislature') &&
                  matchesRaceSearch(raceSearchTitles['tx-house']) && (
                    <Contest
                      key={matches['tx-house'].id}
                      contest={matches['tx-house']}
                      tableHeader={CandidateRow.Header}
                      columnCount={CandidateRow.columnCount}
                      notes={noteMatches['tx-house'].filter(
                        item => item.type === matches['tx-house'][0].office_slug
                      )}
                      contestTitle={determineContestTitleYourRaces(
                        matches['tx-house'][0]
                      )}
                      renderParty={party => (
                        <SeatPartyDivider
                          partyName={party.party}
                          partyBallot={party}
                          tableHeader={CandidateRow.Header}
                          notes={noteMatches['tx-house'].filter(
                            contest =>
                              contest.type ===
                              party.office_slug + '-' + party.party
                          )}
                          columnCount={CandidateRow.columnCount}
                          renderCandidate={(candidate, idx) => (
                            <CandidateRow
                              key={candidate.id}
                              ballotName={candidate.name}
                              candidateId={candidate.id}
                              isIncumbent={candidate.is_incumbent}
                              party={candidate.party}
                            />
                          )}
                        />
                      )}
                    >
                      <DistrictInfo>
                        <AsyncDistrictStats
                          name="Texas House"
                          district={data['tx_house'].district}
                          slug="texas-house"
                          width={width}
                        />
                        <AsyncDistrictMap
                          slug="texas-house"
                          district={data['tx_house'].district}
                          point={coordinates}
                          width={width}
                        />
                      </DistrictInfo>
                    </Contest>
                  )}
                {shouldShowRace('education') &&
                  matchesRaceSearch(raceSearchTitles['sboe']) &&
                  matches['sboe'].length > 0 && (
                    <Contest
                      key={matches['sboe'][0].office_slug}
                      contest={matches['sboe']}
                      tableHeader={CandidateRow.Header}
                      columnCount={CandidateRow.columnCount}
                      notes={noteMatches['sboe'].filter(
                        item => item.type === matches['sboe'][0].office_slug
                      )}
                      contestTitle={determineContestTitleYourRaces(
                        matches['sboe'][0]
                      )}
                      renderParty={party => (
                        <SeatPartyDivider
                          partyName={party.party}
                          partyBallot={party}
                          tableHeader={CandidateRow.Header}
                          notes={noteMatches['sboe'].filter(
                            contest =>
                              contest.type ===
                              party.office_slug + '-' + party.party
                          )}
                          columnCount={CandidateRow.columnCount}
                          renderCandidate={(candidate, idx) => (
                            <CandidateRow
                              key={candidate.id}
                              ballotName={candidate.name}
                              candidateId={candidate.id}
                              isIncumbent={candidate.is_incumbent}
                              party={candidate.party}
                            />
                          )}
                        />
                      )}
                    >
                      <DistrictInfo>
                        <AsyncDistrictStats
                          name="State Board Of Education"
                          district={data['sboe'].district}
                          slug="state-board-of-education"
                          width={width}
                        />
                        <AsyncDistrictMap
                          slug="state-board-of-education"
                          district={data['sboe'].district}
                          point={coordinates}
                          width={width}
                        />
                      </DistrictInfo>
                    </Contest>
                  )}
              </div>
            </div>
          </Portal>
        )}

        {/* Local races (Branch/VoterGuideOS).
        Own Portal, own DOM node (still inside the gray
        .container-gray-two/.results-container-map box, alongside
        your-races-results) — kept structurally separate from the top-race
        Contest blocks above so a rendering failure here can never affect
        them, same isolation principle as loadBranchBallot. */}
        {branchBallot &&
          (LOCAL_RACE_GROUPS.some(group => shouldShowRace(group.key)) ||
            shouldShowRace('local-measures')) &&
          // A search that matches no local race shouldn't leave the "isn't
          // fully available" message behind
          (!raceSearchTerm ||
            shownLocalRaces.length + shownLocalMeasures.length > 0) && (
            <Portal node={document.getElementById('local-races-results')}>
              <LocalRaces
                races={shownLocalRaces}
                measures={shownLocalMeasures}
                resetKey={branchBallot}
              />
            </Portal>
          )}

        {/* Rendered into its own node below #local-races-results so the note
        reads as the footer of the whole gray box (top races, then local
        races), rather than sitting between them. */}
        {data && contests.length > 0 && (
          <Portal node={document.getElementById('your-races-data-note')}>
            <div class="your-races-data-note">
              <h3 class="your-races-data-note__title">About the data</h3>
              <p class="your-races-data-note__text">
                Demographic data and the 2024 general presidential results are
                from the{' '}
                <a
                  href="https://data.capitol.texas.gov/organization/eb7dd023-f166-4613-92ff-b6b3d5c026c6?groups=redistricting"
                  target="_blank"
                >
                  Texas Legislative Council
                </a>
                . Data on local races and measures is from VoterGuideOS. Local
                races are only available for the 50 most populous counties in
                Texas.
              </p>
            </div>
          </Portal>
        )}
      </form>
    );
  }
}

export default YourRaces;
